(() => {
  const TAG = 'robot-selected-package';
  if (customElements.get(TAG)) return;

  const normalise = value =>
    String(value || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[–—-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const packageName = value =>
    String(value || '')
      .trim()
      .replace(/:\s*$/, '')
      .replace(/\s*(?:contains|includes|included|contents)\s*$/i, '')
      .trim();

  const isOptionalHeading = value =>
    /^\s*(?:optional\b|add[\s-]?ons?\b|upgrades?\b)/i.test(value);

  const isContentsHeading = value =>
    !isOptionalHeading(value) &&
    /\b(?:contains|includes|included|contents)\b/i.test(value);

  function packageKey(value) {
    const text = normalise(value);

    if (/\bpartial\s*(?:carbon|cf)\b/.test(text)) return 'partial carbon';
    if (/\bfull\s*(?:carbon|cf)\b/.test(text)) return 'full carbon';

    if (/\bdry\s*(?:carbon|cf)\b|\bpre\s*preg\b/.test(text)) {
      return 'dry carbon';
    }

    if (/\bstandard\b/.test(text)) return 'standard';
    if (/\bfrp\b|\bfibreglass\b|\bfiberglass\b/.test(text)) return 'frp';
    if (/\bcarbon\b/.test(text)) return 'carbon';
    if (/\babs\b/.test(text)) return 'abs';
    if (/\bpolypropylene\b|\bpp\b/.test(text)) return 'pp';

    return null;
  }

  function hasContent(node) {
    if (node.nodeType === 8) return false;

    return Boolean(
      node.textContent.trim() ||
      (
        node.nodeType === 1 &&
        (
          node.matches('img,iframe,table,video') ||
          node.querySelector('img,iframe,table,video')
        )
      )
    );
  }

  function getHeading(node) {
    if (node.nodeType !== 1) return null;

    if (
      /^H[1-6]$/.test(node.tagName) ||
      node.getAttribute('role') === 'heading' ||
      ['STRONG', 'B'].includes(node.tagName)
    ) {
      return {
        title: node.textContent.trim(),
        rest: null
      };
    }

    if (!['P', 'DIV'].includes(node.tagName)) return null;

    const bold = node.querySelector('strong,b');
    if (!bold) return null;

    const title = bold.textContent.trim();

    if (
      !title ||
      title.length > 170 ||
      !node.textContent.trim().startsWith(title)
    ) {
      return null;
    }

    if (
      !/contains|includes|included|contents|fitment|fitting|installation|shipping|delivery|production|compatib|specification|wheel spec|fits the following|optional|add.?ons|upgrades/i.test(title)
    ) {
      return null;
    }

    const rest = node.cloneNode(true);
    rest.querySelector('strong,b').remove();

    return {
      title,
      rest: hasContent(rest) ? rest : null
    };
  }

  function readGroups(source) {
    if (source.querySelector('script,style,form')) return null;

    const nodes = [];

    function visit(node) {
      if (
        node.nodeType === 1 &&
        node.tagName === 'DIV' &&
        node.getAttribute('role') !== 'heading' &&
        node.querySelector('h1,h2,h3,h4,h5,h6,p,ul,ol,div')
      ) {
        [...node.childNodes].forEach(visit);
      } else {
        nodes.push(node);
      }
    }

    [...source.childNodes].forEach(visit);

    const groups = [];
    let current = { title: 'Overview', nodes: [] };

    nodes.forEach(node => {
      const heading = getHeading(node);

      if (heading) {
        if (current.nodes.some(hasContent)) {
          groups.push(current);
        }

        current = {
          title: heading.title,
          nodes: heading.rest ? [heading.rest] : []
        };
      } else if (hasContent(node)) {
        current.nodes.push(node.cloneNode(true));
      }
    });

    if (current.nodes.some(hasContent)) {
      groups.push(current);
    }

    if (!groups.some(group => isContentsHeading(group.title))) {
      return null;
    }

    return groups.map(group => ({
      ...group,
      contents: isContentsHeading(group.title),
      optional: isOptionalHeading(group.title),
      key: packageKey(group.title)
    }));
  }

  class RobotSelectedPackage extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        this.watch();
        this.update();
        return;
      }

      const source = this.querySelector('[data-package-source]');
      const json = this.querySelector('[data-package-data]');

      if (!source || !json) {
        if (!this.waiting) {
          this.waiting = new MutationObserver(() => {
            this.connectedCallback();
          });

          this.waiting.observe(this, {
            childList: true,
            subtree: true
          });
        }

        return;
      }

      this.waiting?.disconnect();

      try {
        this.data = JSON.parse(json.textContent);
      } catch {
        return;
      }

      this.packagePrefix = this.id.replace(/[^a-zA-Z0-9_-]/g, '');
      this.groups = readGroups(source);
      this.started = true;

      if (this.groups) {
        this.buildAccordions();
        source.remove();
        this.querySelector('[data-package-built]').hidden = false;
      }

      this.onChange = event => {
        if (!this.findForm()?.contains(event.target)) return;

        clearTimeout(this.changeTimer);

        this.changeTimer = setTimeout(() => {
          this.eventVariantId = undefined;
          this.update();
        }, 120);
      };

      this.watch();
      this.update();
    }

    disconnectedCallback() {
      clearInterval(this.poll);
      clearTimeout(this.changeTimer);
      this.waiting?.disconnect();

      if (this.onChange) {
        document.removeEventListener('change', this.onChange);
      }

      this.poll = null;
    }

    findForm() {
      const forms = document.querySelectorAll(
        'form[data-js-product-form][data-template]'
      );

      for (const form of forms) {
        try {
          const product = JSON.parse(
            form.querySelector('[data-js-product-json]')?.textContent || '{}'
          );

          if (String(product.id) === this.dataset.productId) {
            return form;
          }
        } catch {}
      }

      return [...document.querySelectorAll('section[data-product-handle]')]
        .find(section =>
          section.dataset.productHandle === this.dataset.productHandle
        )
        ?.querySelector('form');
    }

    watch() {
      if (this.onChange) {
        document.removeEventListener('change', this.onChange);
        document.addEventListener('change', this.onChange);
      }

      if (!this.poll) {
        this.poll = setInterval(() => {
          this.connectBus();
          this.update();
        }, 500);
      }

      this.connectBus();
    }

    connectBus() {
      const sectionId = this.findForm()?.dataset.sectionId;
      const bus = window.themeCore?.EventBus;

      if (
        this.busConnected ||
        !sectionId ||
        typeof bus?.listen !== 'function'
      ) {
        return;
      }

      this.busConnected = true;

      bus.listen(
        `pdp:section-${sectionId}:change-variant`,
        payload => {
          if (!this.isConnected) return;

          this.eventVariantId = payload?.variant?.id ?? null;
          this.update();
        }
      );
    }

    getVariant() {
      const form = this.findForm();

      const field =
        form?.querySelector('[name="id"]:checked') ||
        form?.querySelector('[name="id"]');

      if (this.eventVariantId !== undefined) {
        if (String(field?.value) === String(this.eventVariantId)) {
          this.eventVariantId = undefined;
        } else {
          return this.data.variants.find(
            variant =>
              String(variant.id) === String(this.eventVariantId)
          ) || null;
        }
      }

      const id = field ? field.value : this.dataset.initialVariant;

      return this.data.variants.find(
        variant => String(variant.id) === String(id)
      ) || null;
    }

    createAccordion(title, id) {
      const element = document.createElement('details');
      const summary = document.createElement('summary');
      const body = document.createElement('div');

      element.className = 'rp-accordion';
      element.id = id;
      summary.textContent = title.replace(/:\s*$/, '');
      body.className = 'rp-accordion-body rte';

      element.append(summary, body);

      return { element, body };
    }

    buildAccordions() {
      const nav = this.querySelector('[data-package-nav]');
      const target = this.querySelector('[data-package-accordions]');
      const contents = this.querySelector('[data-package-contents]');

      nav.replaceChildren();
      target.replaceChildren();

      const link = (label, id) => {
        const anchor = document.createElement('a');
        anchor.href = '#' + id;
        anchor.textContent = label;
        nav.append(anchor);
      };

      contents.id = this.packagePrefix + '-contents';
      link("What's included", contents.id);

      const kits = this.groups.filter(group => group.contents);

      if (kits.length > 1) {
        const comparison = this.createAccordion(
          'Compare package contents',
          this.packagePrefix + '-comparison'
        );

        kits.forEach(group => {
          const heading = document.createElement('h3');
          heading.textContent = group.title;

          comparison.body.append(
            heading,
            ...group.nodes.map(node => node.cloneNode(true))
          );
        });

        target.append(comparison.element);
        this.comparison = comparison.element;
        link('Compare kits', comparison.element.id);
      }

      let fitmentLinked = false;
      let deliveryLinked = false;

      this.groups
        .filter(group => !group.contents)
        .forEach((group, index) => {
          const accordion = this.createAccordion(
            group.optional ? 'Available upgrades' : group.title,
            this.packagePrefix + '-details-' + index
          );

          accordion.body.append(
            ...group.nodes.map(node => node.cloneNode(true))
          );

          target.append(accordion.element);

          if (
            !fitmentLinked &&
            /fitment|fitting|installation|compatib|fits the following/i.test(
              group.title
            )
          ) {
            link('Fitment & installation', accordion.element.id);
            fitmentLinked = true;
          }

          if (
            !deliveryLinked &&
            /shipping|delivery/i.test(group.title)
          ) {
            link('Delivery', accordion.element.id);
            deliveryLinked = true;
          }
        });

      nav.hidden = !nav.children.length;

      nav.addEventListener('click', event => {
        const anchor = event.target.closest('a');
        if (!anchor) return;

        const element = document.getElementById(
          anchor.hash.substring(1)
        );

        if (element?.tagName === 'DETAILS') {
          element.open = true;
        }
      });
    }

   drawContents(group) {
  const target = this.querySelector('[data-package-contents]');

  // Preserve the original description wording and formatting.
  target.replaceChildren(
    ...group.nodes.map(node => node.cloneNode(true))
  );

  // Automatically detect shipping methods in included parts.
  target.querySelectorAll('li').forEach(item => {
    const text = item.textContent
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    item.removeAttribute('data-shipping');

    // Sea freight
    if (/\b(sea freight|ocean freight|sea shipping|shipping by sea)\b/.test(text)) {
      item.setAttribute('data-shipping', 'sea');
    }

    // Air freight, courier or express delivery
    else if (/\b(air freight|air shipping|air delivery|courier|express shipping|express delivery)\b/.test(text)) {
      item.setAttribute('data-shipping', 'air');
    }
  });
}

    update() {
      if (!this.isConnected || !this.data) return;

      const variant = this.getVariant();
      const state = variant ? String(variant.id) : 'unavailable';

      if (state === this.lastState) return;
      this.lastState = state;

      this.querySelector('[data-package-selection]').textContent =
        variant
          ? (variant.title === 'Default Title' ? '' : variant.title)
          : 'Choose an available option above';

      const image = this.querySelector('.rp-image');
      const src = variant?.image || this.data.defaultImage;

      if (image && src) {
        if (image.getAttribute('src') !== src) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = src;
        }

        image.alt = variant?.image
          ? variant.alt || variant.title
          : this.data.defaultAlt;
      }

      const figure = this.querySelector('.rp-figure');
      if (figure) figure.hidden = !image || !src;

      this.querySelector('.rp-layout').classList.toggle(
        'rp-without-image',
        !image || !src
      );

      if (!this.groups) return;

      const title = this.querySelector('[data-package-title]');
      const contents = this.querySelector('[data-package-contents]');

      const kits = this.groups.filter(
        group => group.contents && group.nodes.some(hasContent)
      );

      let index = this.data.options.findIndex(
        name => /material|construction/i.test(name)
      );

      if (index < 0) {
        index = this.data.options.findIndex(
          name => /version|style|package/i.test(name)
        );
      }

      const optionText = variant
        ? (
            index >= 0
              ? variant.options[index]
              : variant.options.join(' ')
          )
        : '';

      const key = packageKey(optionText);
      const selectedName = normalise(packageName(optionText));

      const exact = selectedName
        ? kits.filter(
            group =>
              normalise(packageName(group.title)) === selectedName
          )
        : [];

      const compound = /\+|\bwith\b/i.test(optionText);

      const matches = exact.length
        ? exact
        : (
            !compound && key
              ? kits.filter(group => group.key === key)
              : []
          );

      let chosen = matches.length === 1 ? matches[0] : null;

      if (
        !chosen &&
        variant &&
        !compound &&
        kits.length === 1 &&
        !kits[0].key
      ) {
        chosen = kits[0];
      }

      if (chosen) {
        const selectedTitle =
          variant.title === 'Default Title'
            ? packageName(chosen.title)
            : variant.title;

        title.textContent = selectedTitle;
        this.drawContents(chosen);
      } else {
        title.textContent = variant
          ? (
              variant.title === 'Default Title'
                ? 'Package contents'
                : variant.title + ' — Package contents'
            )
          : 'Choose your package';

        const message = document.createElement('p');

        message.textContent = variant
          ? 'A separate contents list is not available for this selection. Please check the package details below or contact us to confirm.'
          : 'Select an available option above to see its contents.';

        contents.replaceChildren(message);

        if (this.comparison) {
          this.comparison.open = Boolean(variant);
        } else if (kits.length) {
          // Preserve the original list without presenting it as a
          // confirmed match for the selected variant.
          if (!this.fallbackDetails) {
            const fallback = this.createAccordion(
              'Package details from the description',
              this.packagePrefix + '-original-details'
            );

            kits.forEach(group => {
              const heading = document.createElement('h3');
              heading.textContent = group.title;

              fallback.body.append(
                heading,
                ...group.nodes.map(node => node.cloneNode(true))
              );
            });

            this.querySelector('[data-package-accordions]')
              .prepend(fallback.element);

            this.fallbackDetails = fallback.element;
          }

          this.fallbackDetails.hidden = false;
          this.fallbackDetails.open = Boolean(variant);
        }
      }

      if (this.fallbackDetails && chosen) {
        this.fallbackDetails.hidden = true;
      }

      // Let the separate upgrades summary refresh after contents change.
      this.dispatchEvent(
        new CustomEvent('robot:package-updated', { bubbles: true })
      );
    }
  }

  customElements.define(TAG, RobotSelectedPackage);
})();